import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Calendar, User, Clock, Building2, FileText, ChevronRight, ChevronLeft } from 'lucide-react';
import { calendarApi, CalendarEvent } from '../../services/calendarService';
import { generateTwoWeeksGrid, groupEventsByDate, getShortHebrewDayName, getEventIcon, formatTime, getHebrewMonthName } from '../../utils/calendarHelpers';

const Widget = styled.div`
  background: white;
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const TitleIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const Title = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavControls = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: white;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
    background: #f8fafc;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DateRangeLabel = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  min-width: 120px;
  text-align: center;
`;

const TodayButton = styled.button`
  padding: 6px 12px;
  border: 1px solid var(--primary-color);
  border-radius: 6px;
  background: transparent;
  color: var(--primary-color);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
`;

const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  color: var(--text-secondary);
`;

const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 12px;
`;

const DayHeaderCell = styled.div`
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary);
  padding: 6px 0;
  text-transform: uppercase;
  letter-spacing: 0.3px;
`;

const DayCell = styled.div<{ $isToday: boolean; $hasEvents: boolean }>`
  height: 120px;
  padding: 10px;
  background: ${props => props.$isToday ? '#f8fafc' : 'white'};
  border: 1px solid var(--border-color);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.2s ease;

  ${props => props.$isToday && `
    border-color: var(--primary-color);
    box-shadow: inset 0 0 0 1px var(--primary-color)40;
  `}

  &:hover {
    background: ${props => props.$isToday ? '#f0f7ff' : '#f8fafc'};
    border-color: ${props => props.$isToday ? 'var(--primary-color)' : '#c0c6cc'};
  }
`;

const DayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DayNumber = styled.div<{ $isToday: boolean }>`
  font-size: 14px;
  font-weight: ${props => props.$isToday ? '700' : '500'};
  color: ${props => props.$isToday ? 'var(--primary-color)' : 'var(--text-primary)'};
`;

const TodayBadge = styled.span`
  font-size: 9px;
  font-weight: 600;
  color: white;
  background: var(--primary-color);
  padding: 2px 6px;
  border-radius: 4px;
`;

const EventsContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3px;
  overflow-y: auto;
  margin-top: 4px;

  &::-webkit-scrollbar {
    width: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 2px;
  }
`;

const EventPill = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  background: transparent;
  border-right: 3px solid ${props => props.$color};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #f5f7f9;
  }
`;

const EventPillIcon = styled.span<{ $color: string }>`
  display: flex;
  align-items: center;
  color: ${props => props.$color};
  filter: saturate(0.8) brightness(0.95);
`;

const EventPillText = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
`;

const MoreButton = styled.button`
  padding: 3px 8px;
  background: #f5f7f9;
  border: none;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 500;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    background: var(--primary-color);
    color: white;
  }
`;

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: var(--surface-color);
  border-radius: var(--border-radius-lg);
  padding: 24px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px 8px;
  line-height: 1;
  transition: color 0.2s ease;

  &:hover {
    color: var(--text-primary);
  }
`;

const ModalEventList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalEventItem = styled.div<{ color: string }>`
  padding: 12px;
  border-right: 4px solid ${props => props.color};
  background: transparent;
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f5f7f9;
    transform: translateX(-2px);
  }
`;

const ModalEventHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const ModalEventTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ModalEventInfo = styled.div`
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const CalendarWidget: React.FC = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalEvents, setModalEvents] = useState<CalendarEvent[]>([]);
  const [modalDate, setModalDate] = useState<Date | null>(null);
  const [weekOffset, setWeekOffset] = useState(0);

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const sunday = new Date(today);
      sunday.setDate(today.getDate() - today.getDay() + (weekOffset * 7));

      const endDate = new Date(sunday);
      endDate.setDate(sunday.getDate() + 13);

      const formatDate = (date: Date) => date.toISOString().split('T')[0];
      const data = await calendarApi.getCalendarEvents(
        formatDate(sunday),
        formatDate(endDate)
      );
      setEvents(data);
    } catch (error: any) {
      console.error('Error loading calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateRangeLabel = (): string => {
    const today = new Date();
    const sunday = new Date(today);
    sunday.setDate(today.getDate() - today.getDay() + (weekOffset * 7));

    const endDate = new Date(sunday);
    endDate.setDate(sunday.getDate() + 13);

    const startMonth = getHebrewMonthName(sunday.getMonth());
    const endMonth = getHebrewMonthName(endDate.getMonth());

    if (sunday.getMonth() === endDate.getMonth()) {
      return `${sunday.getDate()}-${endDate.getDate()} ${startMonth}`;
    }
    return `${sunday.getDate()} ${startMonth} - ${endDate.getDate()} ${endMonth}`;
  };

  const handlePrev = () => setWeekOffset(prev => prev - 2);
  const handleNext = () => setWeekOffset(prev => prev + 2);
  const handleToday = () => setWeekOffset(0);

  const eventsMap = groupEventsByDate(events);
  const cells = generateTwoWeeksGrid(eventsMap, weekOffset);
  const dayNames = [0, 1, 2, 3, 4, 5, 6].map(getShortHebrewDayName);

  const handleMoreClick = (dayEvents: CalendarEvent[], date: Date) => {
    setModalEvents(dayEvents);
    setModalDate(date);
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalEvents([]);
    setModalDate(null);
  };

  const handleModalEventClick = (clientId: number) => {
    handleModalClose();
    navigate(`/clients/${clientId}/manage`);
  };

  if (loading) {
    return (
      <Widget>
        <Header>
          <TitleContainer>
            <TitleIcon>
              <Calendar size={20} />
            </TitleIcon>
            <Title>לוח שנה</Title>
          </TitleContainer>
        </Header>
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
          טוען...
        </div>
      </Widget>
    );
  }

  return (
    <Widget>
      <Header>
        <TitleContainer>
          <TitleIcon>
            <Calendar size={20} />
          </TitleIcon>
          <Title>לוח שנה</Title>
        </TitleContainer>
        <HeaderControls>
          <NavControls>
            <NavButton onClick={handlePrev} title="שבועיים קודמים">
              <ChevronRight size={16} />
            </NavButton>
            <DateRangeLabel>{getDateRangeLabel()}</DateRangeLabel>
            <NavButton onClick={handleNext} title="שבועיים הבאים">
              <ChevronLeft size={16} />
            </NavButton>
          </NavControls>
          {weekOffset !== 0 && (
            <TodayButton onClick={handleToday}>היום</TodayButton>
          )}
        </HeaderControls>
      </Header>

      {/* Day headers */}
      <WeekGrid>
        {dayNames.map((day, index) => (
          <DayHeaderCell key={index}>{day}</DayHeaderCell>
        ))}
      </WeekGrid>

      {/* Week 1 grid */}
      <WeekGrid>
        {cells.slice(0, 7).map((cell, cellIndex) => (
          <DayCell
            key={cellIndex}
            $isToday={cell.isToday}
            $hasEvents={cell.events.length > 0}
          >
            <DayHeader>
              <DayNumber $isToday={cell.isToday}>
                {cell.date.getDate()}
              </DayNumber>
              {cell.isToday && <TodayBadge>היום</TodayBadge>}
            </DayHeader>

            <EventsContainer>
              {cell.displayedEvents.map(event => (
                <EventPill
                  key={event.id}
                  $color={event.color}
                  onClick={() => handleMoreClick(cell.events, cell.date)}
                >
                  <EventPillIcon $color={event.color}>
                    {getEventIcon(event)}
                  </EventPillIcon>
                  <EventPillText>
                    {event.clientName || event.title}
                  </EventPillText>
                </EventPill>
              ))}

              {cell.hasMore && (
                <MoreButton onClick={() => handleMoreClick(cell.events, cell.date)}>
                  +{cell.events.length - 3} נוספים
                </MoreButton>
              )}
            </EventsContainer>
          </DayCell>
        ))}
      </WeekGrid>

      {/* Week 2 */}
      <WeekGrid style={{ marginTop: '12px' }}>
        {dayNames.map((day, index) => (
          <DayHeaderCell key={`week2-${index}`}>{day}</DayHeaderCell>
        ))}
      </WeekGrid>

      <WeekGrid>
        {cells.slice(7, 14).map((cell, cellIndex) => (
          <DayCell
            key={cellIndex}
            $isToday={cell.isToday}
            $hasEvents={cell.events.length > 0}
          >
            <DayHeader>
              <DayNumber $isToday={cell.isToday}>
                {cell.date.getDate()}
              </DayNumber>
              {cell.isToday && <TodayBadge>היום</TodayBadge>}
            </DayHeader>

            <EventsContainer>
              {cell.displayedEvents.map(event => (
                <EventPill
                  key={event.id}
                  $color={event.color}
                  onClick={() => handleMoreClick(cell.events, cell.date)}
                >
                  <EventPillIcon $color={event.color}>
                    {getEventIcon(event)}
                  </EventPillIcon>
                  <EventPillText>
                    {event.clientName || event.title}
                  </EventPillText>
                </EventPill>
              ))}

              {cell.hasMore && (
                <MoreButton onClick={() => handleMoreClick(cell.events, cell.date)}>
                  +{cell.events.length - 3} נוספים
                </MoreButton>
              )}
            </EventsContainer>
          </DayCell>
        ))}
      </WeekGrid>

      {/* Modal for showing all events on a day */}
      <ModalOverlay $isOpen={modalOpen} onClick={handleModalClose}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>
              {modalDate && `כל האירועים ליום ${modalDate.getDate()}/${modalDate.getMonth() + 1}`}
            </ModalTitle>
            <CloseButton onClick={handleModalClose}>×</CloseButton>
          </ModalHeader>

          <ModalEventList>
            {modalEvents.map(event => (
              <ModalEventItem
                key={event.id}
                color={event.color}
                onClick={() => event.clientId && handleModalEventClick(event.clientId)}
              >
                <ModalEventHeader>
                  <ModalEventTitle>
                    <span>{getEventIcon(event)}</span>
                    <span>{event.title}</span>
                  </ModalEventTitle>
                </ModalEventHeader>
                <ModalEventInfo>
                  {event.clientName && (
                    <InfoRow>
                      <InfoIcon><User size={14} /></InfoIcon>
                      {event.clientName}
                    </InfoRow>
                  )}
                  <InfoRow>
                    <InfoIcon><Clock size={14} /></InfoIcon>
                    {formatTime(event.startDateTime)}
                  </InfoRow>
                  {event.doctorName && (
                    <InfoRow>
                      <InfoIcon><Building2 size={14} /></InfoIcon>
                      {event.doctorName}
                    </InfoRow>
                  )}
                  {event.description && (
                    <InfoRow>
                      <InfoIcon><FileText size={14} /></InfoIcon>
                      {event.description}
                    </InfoRow>
                  )}
                </ModalEventInfo>
              </ModalEventItem>
            ))}
          </ModalEventList>
        </ModalContent>
      </ModalOverlay>
    </Widget>
  );
};

export default CalendarWidget;
